import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { EmailComposer } from "@/components/email-composer";
import {
  Mail, RefreshCw, User, UserPlus, X, Check,
  ChevronLeft, Paperclip, ExternalLink, Reply, ChevronDown, ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/cn";

function relativeTime(dateStr: string) {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

interface InboxMessage {
  id: string;
  subject: string;
  from: { emailAddress: { name: string; address: string } };
  toRecipients: { emailAddress: { address: string } }[];
  receivedDateTime: string;
  bodyPreview: string;
  isRead: boolean;
  hasAttachments: boolean;
  conversationId?: string;
  _lead: { id: string; name: string; email: string } | null;
  _contact: { id: string; name: string; email: string } | null;
}

interface ThreadMessage {
  id: string;
  subject: string;
  from: { emailAddress: { name: string; address: string } };
  toRecipients: { emailAddress: { address: string } }[];
  receivedDateTime: string;
  bodyPreview?: string;
  body?: { content: string; contentType: string };
  isRead: boolean;
  _isSent: boolean;
}

/** Single collapsible message card inside the thread */
function ThreadCard({
  msg,
  defaultOpen,
}: {
  msg: ThreadMessage;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const sender = msg.from?.emailAddress;
  const initial = (sender?.name ?? sender?.address ?? "?")[0].toUpperCase();

  return (
    <div className={cn(
      "border-b last:border-b-0 transition-colors",
      msg._isSent ? "bg-primary/[0.03]" : "bg-background",
    )}>
      {/* Header row — always visible, click to toggle */}
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Avatar */}
        <div className={cn(
          "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5",
          msg._isSent
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}>
          {msg._isSent ? "You" : initial}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {msg._isSent ? "You" : (sender?.name || sender?.address)}
            </span>
            {msg._isSent && (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 gap-0.5">
                <Reply className="h-2.5 w-2.5" /> Sent
              </Badge>
            )}
            {!msg.isRead && !msg._isSent && (
              <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
            )}
          </div>
          {!open && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate pr-4">
              {msg.bodyPreview || "…"}
            </p>
          )}
          {open && (
            <p className="text-xs text-muted-foreground mt-0.5">
              To: {msg.toRecipients?.map((r) => r.emailAddress.address).join(", ")}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-muted-foreground">
            {formatDateTime(msg.receivedDateTime)}
          </span>
          {open
            ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="px-4 pb-4 pt-0 pl-[3.75rem]">
          {msg.body?.content ? (
            <div
              className="prose prose-sm max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: msg.body.content }}
            />
          ) : (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {msg.bodyPreview}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyInboxPage() {
  const qc = useQueryClient();
  const [q, setQ]                       = useState("");
  const [skip, setSkip]                 = useState(0);
  const [selected, setSelected]         = useState<InboxMessage | null>(null);
  const [thread, setThread]             = useState<ThreadMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);

  // Create Lead modal state
  const [createLead, setCreateLead]     = useState(false);
  const [leadName, setLeadName]         = useState("");
  const [leadEmail, setLeadEmail]       = useState("");
  const [leadCompany, setLeadCompany]   = useState("");
  const [leadTitle, setLeadTitle]       = useState("");
  const [leadDone, setLeadDone]         = useState<string | null>(null);

  // Reply composer
  const [showReply, setShowReply]       = useState(false);

  const PAGE_SIZE = 25;

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["/graph/inbox", q, skip],
    queryFn: () =>
      api.get("/graph/inbox", { params: { top: PAGE_SIZE, skip, ...(q ? { q } : {}) } }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const messages: InboxMessage[] = data?.value ?? [];
  const hasMore = !!data?.["@odata.nextLink"];

  async function openMessage(msg: InboxMessage) {
    setSelected(msg);
    setThread([]);
    setLoadingThread(true);
    setCreateLead(false);
    setLeadDone(null);
    try {
      // Fetch single message (for its HTML body) + full thread in parallel
      const requests: Promise<any>[] = [api.get(`/graph/inbox/${msg.id}`)];
      if (msg.conversationId) {
        requests.push(api.get(`/graph/inbox/thread/${msg.conversationId}`));
      }
      const [msgRes, threadRes] = await Promise.all(requests);
      const fullMsg = msgRes.data;
      const threadMsgs: ThreadMessage[] = threadRes?.data?.value ?? [];

      // If thread has multiple messages use it; otherwise fall back to single message
      if (threadMsgs.length > 1) {
        // Ensure the opened message has its full body
        const enriched = threadMsgs.map((m) =>
          m.id === msg.id ? { ...m, body: fullMsg.body } : m,
        );
        setThread(enriched);
      } else {
        // Single message thread — just show the full body directly
        setThread([{ ...msg, ...fullMsg, _isSent: false }]);
      }
    } catch {
      // fall back to preview only
      setThread([{ ...msg, _isSent: false }]);
    } finally {
      setLoadingThread(false);
    }
  }

  function openCreateLead(msg: InboxMessage) {
    const sender = msg.from?.emailAddress;
    setLeadName(sender?.name ?? "");
    setLeadEmail(sender?.address ?? "");
    setLeadCompany("");
    setLeadTitle("");
    setLeadDone(null);
    setCreateLead(true);
  }

  const createLeadMut = useMutation({
    mutationFn: () =>
      api.post("/leads", {
        name:    leadName || leadEmail,
        email:   leadEmail,
        company: leadCompany || undefined,
        title:   leadTitle  || undefined,
        source:  "My Inbox",
        notes:   `Created from inbox email: "${selected?.subject}"`,
      }).then((r) => r.data),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: ["/leads"] });
      qc.invalidateQueries({ queryKey: ["/graph/inbox"] });
      setLeadDone(lead.id);
    },
  });

  const notConnected = (error as any)?.response?.data?.error === "EMAIL_NOT_CONNECTED" ||
    (error as any)?.response?.status === 403 || (error as any)?.response?.status === 401;

  // Derive reply status from the loaded thread
  const hasReplied = thread.some((m) => m._isSent);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="My Inbox"
        description="Your personal Microsoft 365 inbox — pulled live from Outlook."
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", isRefetching && "animate-spin")} />
            Refresh
          </Button>
        }
      />

      {/* Not connected / expired banner */}
      {notConnected && (
        <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <span className="font-medium">
            {(error as any)?.response?.data?.message?.includes("expired")
              ? "⚠️ Your Microsoft session has expired."
              : "Your email isn't connected yet."}
          </span>{" "}
          <a href="/login" className="font-medium underline">Sign in with Microsoft again</a> to{" "}
          {(error as any)?.response?.data?.message?.includes("expired") ? "reconnect" : "link your Microsoft 365 inbox"}.
        </div>
      )}

      {!notConnected && (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* ── Left: message list ── */}
          <div className={cn("flex flex-col border-r w-full", selected && "hidden md:flex md:w-80 lg:w-96 shrink-0")}>
            {/* Search */}
            <div className="px-3 py-2 border-b bg-card">
              <Input
                placeholder="Search subject or sender…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setSkip(0); setSelected(null); }}
                className="h-8"
              />
            </div>

            <div className="flex-1 overflow-auto">
              {isLoading && (
                <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>
              )}
              {!isLoading && messages.length === 0 && !notConnected && (
                <div className="p-10 text-center">
                  <Mail className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-muted-foreground text-sm">No emails found.</p>
                </div>
              )}
              <ul className="divide-y">
                {messages.map((msg) => {
                  const sender = msg.from?.emailAddress;
                  const isActive = selected?.id === msg.id;
                  return (
                    <li
                      key={msg.id}
                      onClick={() => openMessage(msg)}
                      className={cn(
                        "px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors",
                        isActive && "bg-primary/5 border-l-2 border-primary",
                      )}
                    >
                      <div className="flex items-start justify-between gap-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {!msg.isRead && (
                            <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-0.5" />
                          )}
                          <span className={cn("text-sm truncate", !msg.isRead && "font-semibold")}>
                            {sender?.name || sender?.address || "Unknown"}
                          </span>
                        </div>
                        <span className="text-[11px] text-muted-foreground shrink-0 mt-0.5">
                          {relativeTime(msg.receivedDateTime)}
                        </span>
                      </div>
                      <p className={cn("text-xs mt-0.5 truncate", !msg.isRead ? "text-foreground" : "text-muted-foreground")}>
                        {msg.subject || "(no subject)"}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <p className="text-[11px] text-muted-foreground line-clamp-1 flex-1">
                          {msg.bodyPreview}
                        </p>
                        {msg.hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />}
                        {msg._lead && (
                          <Badge variant="secondary" className="text-[10px] py-0 shrink-0">
                            Lead: {msg._lead.name}
                          </Badge>
                        )}
                        {msg._contact && !msg._lead && (
                          <Badge variant="secondary" className="text-[10px] py-0 shrink-0">
                            <User className="h-2.5 w-2.5 mr-0.5" />{msg._contact.name}
                          </Badge>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Pagination */}
              {(skip > 0 || hasMore) && (
                <div className="flex items-center justify-between px-3 py-2 border-t text-xs text-muted-foreground">
                  <button
                    className="hover:text-foreground disabled:opacity-40"
                    disabled={skip === 0}
                    onClick={() => { setSkip(Math.max(0, skip - PAGE_SIZE)); setSelected(null); }}
                  >
                    ← Newer
                  </button>
                  <button
                    className="hover:text-foreground disabled:opacity-40"
                    disabled={!hasMore}
                    onClick={() => { setSkip(skip + PAGE_SIZE); setSelected(null); }}
                  >
                    Older →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: thread detail ── */}
          {selected && (
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {/* Detail header */}
              <div className="px-4 py-3 border-b bg-card flex items-start justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    onClick={() => { setSelected(null); setThread([]); }}
                    className="md:hidden text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-semibold text-sm truncate">{selected.subject || "(no subject)"}</h2>
                      {hasReplied && (
                        <Badge variant="secondary" className="text-[10px] py-0 gap-0.5 shrink-0">
                          <Reply className="h-2.5 w-2.5" /> You replied
                        </Badge>
                      )}
                      {thread.length > 1 && (
                        <span className="text-[11px] text-muted-foreground shrink-0">
                          {thread.length} messages
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      From: {selected.from?.emailAddress?.name}{" "}
                      <span className="text-muted-foreground/70">&lt;{selected.from?.emailAddress?.address}&gt;</span>
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {selected._lead && (
                    <a href={`/leads/${selected._lead.id}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="text-xs h-7">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Lead
                      </Button>
                    </a>
                  )}
                  {selected._contact && !selected._lead && (
                    <a href={`/contacts/${selected._contact.id}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="text-xs h-7">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Contact
                      </Button>
                    </a>
                  )}
                  {!selected._lead && !selected._contact && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 border-dashed"
                      onClick={() => openCreateLead(selected)}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Create Lead
                    </Button>
                  )}
                  <Button size="sm" className="text-xs h-7" onClick={() => setShowReply(true)}>
                    <Reply className="h-3 w-3 mr-1" />
                    Reply
                  </Button>
                </div>
              </div>

              {/* Thread / body */}
              <div className="flex-1 overflow-auto">
                {loadingThread && (
                  <div className="p-6 text-sm text-muted-foreground">Loading conversation…</div>
                )}

                {!loadingThread && thread.length > 0 && (
                  <div className="divide-y">
                    {thread.map((m, i) => (
                      <ThreadCard
                        key={m.id}
                        msg={m}
                        defaultOpen={i === 0}  /* newest (index 0) starts expanded */
                      />
                    ))}
                  </div>
                )}

                {!loadingThread && thread.length === 0 && (
                  <p className="p-4 text-sm text-muted-foreground whitespace-pre-wrap">
                    {selected.bodyPreview}
                  </p>
                )}
              </div>

              {/* Create Lead panel */}
              {createLead && (
                <div className="border-t p-4 bg-muted/30 shrink-0">
                  {leadDone ? (
                    <div className="flex items-center gap-2 text-green-700 text-sm">
                      <Check className="h-4 w-4" />
                      Lead created!{" "}
                      <a href={`/leads/${leadDone}`} className="underline font-medium">View Lead →</a>
                      <button onClick={() => setCreateLead(false)} className="ml-auto text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Create Lead from this email</p>
                        <button onClick={() => setCreateLead(false)} className="text-muted-foreground hover:text-foreground">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input value={leadName} onChange={(e) => setLeadName(e.target.value)} className="h-7 text-xs mt-0.5" />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} className="h-7 text-xs mt-0.5" />
                        </div>
                        <div>
                          <Label className="text-xs">Company</Label>
                          <Input value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)} className="h-7 text-xs mt-0.5" placeholder="Optional" />
                        </div>
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input value={leadTitle} onChange={(e) => setLeadTitle(e.target.value)} className="h-7 text-xs mt-0.5" placeholder="Optional" />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => createLeadMut.mutate()}
                        disabled={!leadEmail || createLeadMut.isPending}
                      >
                        {createLeadMut.isPending ? "Creating…" : "Create Lead"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reply composer */}
      <EmailComposer
        open={showReply}
        onClose={() => setShowReply(false)}
        contact={selected ? {
          email: selected.from?.emailAddress?.address,
          name:  selected.from?.emailAddress?.name,
          id:    selected._contact?.id,
        } : undefined}
      />
    </div>
  );
}
